import React from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { GalleryDiorama } from '@/design-system'
import { GallerySummary } from '@/features/album/types'
import { resolveGalleryTint } from '../../utils/galleryTint'

export interface PromoBandProps {
  /** Seeds the mini room preview — any real gallery works, falls back to an empty room. */
  previewGallery?: GallerySummary
  onTry: () => void
  onLearnMore?: () => void
}

const Root = styled(Box)({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 28,
  borderRadius: 20,
  background: 'var(--ds-gradient-spectrum-soft)',
  boxShadow: 'inset 0 0 0 1px var(--ds-border)',
  padding: '26px 32px',
  overflow: 'hidden',
  flexWrap: 'wrap',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 2.5,
    background: 'var(--ds-gradient-spectrum)',
  },
})

const Preview = styled(Box)({
  position: 'relative',
  flexShrink: 0,
  width: 148,
  height: 96,
  borderRadius: 12,
  overflow: 'hidden',
  boxShadow: 'var(--ds-shadow-lift)',
})

const Title = styled(Typography)({
  fontSize: 19,
  fontWeight: 730,
  letterSpacing: '-0.025em',
  marginBottom: 5,
  color: 'var(--ds-ink)',
})

const Desc = styled(Typography)({
  fontSize: 13,
  color: 'var(--ds-ink-muted)',
  lineHeight: 1.55,
  maxWidth: 460,
})

const Actions = styled(Box)({ marginLeft: 'auto', display: 'flex', gap: 10, flexWrap: 'wrap' })

const InkBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  height: 42,
  padding: '0 20px',
  borderRadius: 'var(--ds-radius-pill)',
  cursor: 'pointer',
  background: 'var(--ds-ink)',
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  letterSpacing: '-0.01em',
  transition: 'opacity 260ms var(--ds-ease)',
  '&:hover': { opacity: 0.85 },
})

const GhostBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  height: 42,
  padding: '0 16px',
  borderRadius: 'var(--ds-radius-pill)',
  cursor: 'pointer',
  color: 'var(--ds-ink-muted)',
  fontSize: 14,
  fontWeight: 600,
  transition: 'all 140ms var(--ds-ease)',
  '&:hover': { color: 'var(--ds-ink)', background: 'var(--ds-surface)' },
})

/**
 * Mid-feed marketing band — the one explicit upsell moment on an otherwise
 * content-first page: "turn an album into a walkable room" with a live mini
 * diorama preview, sitting inside the feed rather than above it.
 */
export function PromoBand({ previewGallery, onTry, onLearnMore }: PromoBandProps) {
  const tint = previewGallery ? resolveGalleryTint(previewGallery) : { wallColor: '#e3ddcb', floorColor: '#b6ac97', light: true }

  return (
    <Root>
      <Preview>
        <GalleryDiorama
          size='sm'
          wallColor={tint.wallColor}
          floorColor={tint.floorColor}
          arts={previewGallery?.arts}
          light={tint.light}
        />
      </Preview>
      <Box>
        <Title>Have memories worth a room?</Title>
        <Desc>
          Turn any album into a walkable 3D gallery in about a minute &mdash; pick a room, we hang the prints, guests
          step inside from any link.
        </Desc>
      </Box>
      <Actions>
        <InkBtn onClick={onTry}>&#9672; Try 3D &mdash; it&rsquo;s included</InkBtn>
        {onLearnMore && <GhostBtn onClick={onLearnMore}>See how it works</GhostBtn>}
      </Actions>
    </Root>
  )
}
